import React from 'react';
import { X, MapPin, Briefcase, Building, DollarSign, ExternalLink, Calendar } from 'lucide-react';

interface JobDetailsModalProps {
  job: {
    id: string;
    companyName: string;
    companyLogo?: string | null;
    jobTitle: string;
    location: string;
    workMode: 'Remote' | 'Hybrid' | 'On-site';
    employmentType: 'Full-time' | 'Part-time' | 'Contract';
    seniority?: string;
    description?: string;
    salary?: string | null;
    applyUrl?: string | null;
    postedDate?: string | null;
    requirements?: string[];
    responsibilities?: string[];
    benefits?: string[];
    companyWebsite?: string | null;
  };
  isOpen: boolean;
  onClose: () => void;
}

export const JobDetailsModal: React.FC<JobDetailsModalProps> = ({ job, isOpen, onClose }) => {
  if (!isOpen) return null;

  const handleApply = () => {
    if (job.applyUrl) {
      window.open(job.applyUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return 'Recently';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Recently';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-2 md:p-4">
        <div
          className="relative bg-white rounded-xl md:rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] md:max-h-[90vh] overflow-y-auto animate-fade-in"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white/95 backdrop-blur-md border-b border-slate-100 px-4 md:px-8 py-4 md:py-6 flex items-start justify-between z-10">
            <div className="flex items-start gap-3 md:gap-5 flex-1 min-w-0">
              {/* Company Logo */}
              {job.companyLogo ? (
                <img
                  src={job.companyLogo}
                  alt={job.companyName}
                  className="w-12 h-12 md:w-16 md:h-16 object-contain rounded-xl border border-slate-100 bg-white shadow-sm flex-shrink-0"
                />
              ) : (
                <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-200 flex-shrink-0">
                  <span className="text-white font-bold text-lg md:text-2xl">
                    {job.companyName.charAt(0)}
                  </span>
                </div>
              )}

              {/* Title and Company */}
              <div className="flex-1 min-w-0">
                <h2 className="text-lg md:text-2xl font-bold text-slate-900 mb-1 leading-tight line-clamp-2">
                  {job.jobTitle}
                </h2>
                <div className="flex items-center gap-2 text-slate-600">
                  <Building className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                  <span className="font-medium text-sm md:text-base truncate">{job.companyName}</span>
                  {job.companyWebsite && (
                    <a
                      href={job.companyWebsite}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 ml-1 transition-colors flex-shrink-0"
                    >
                      <ExternalLink className="w-3.5 h-3.5 md:w-4 md:h-4" />
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all p-1.5 md:p-2 rounded-xl ml-2 md:ml-4 flex-shrink-0"
            >
              <X className="w-5 h-5 md:w-6 md:h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="px-4 md:px-8 py-4 md:py-8 space-y-4 md:space-y-8">
            {/* Key Details */}
            <div className="grid grid-cols-2 gap-3 md:gap-6 p-4 md:p-6 bg-slate-50 rounded-xl border border-slate-100">
              <div className="flex items-start gap-2 md:gap-3">
                <div className="p-1.5 md:p-2 bg-white rounded-lg shadow-sm text-blue-600 flex-shrink-0">
                  <MapPin className="w-4 h-4 md:w-5 md:h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] md:text-xs font-semibold text-slate-500 uppercase tracking-wider mb-0.5">Location</div>
                  <div className="font-medium text-slate-900 text-xs md:text-base truncate">{job.location}</div>
                </div>
              </div>

              <div className="flex items-start gap-2 md:gap-3">
                <div className="p-1.5 md:p-2 bg-white rounded-lg shadow-sm text-purple-600 flex-shrink-0">
                  <Briefcase className="w-4 h-4 md:w-5 md:h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] md:text-xs font-semibold text-slate-500 uppercase tracking-wider mb-0.5">Work Mode</div>
                  <div className="font-medium text-slate-900 text-xs md:text-base">{job.workMode}</div>
                </div>
              </div>

              <div className="flex items-start gap-2 md:gap-3">
                <div className="p-1.5 md:p-2 bg-white rounded-lg shadow-sm text-orange-600 flex-shrink-0">
                  <Calendar className="w-4 h-4 md:w-5 md:h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] md:text-xs font-semibold text-slate-500 uppercase tracking-wider mb-0.5">Posted</div>
                  <div className="font-medium text-slate-900 text-xs md:text-base">{formatDate(job.postedDate)}</div>
                </div>
              </div>

              {job.salary && (
                <div className="flex items-start gap-2 md:gap-3">
                  <div className="p-1.5 md:p-2 bg-white rounded-lg shadow-sm text-green-600 flex-shrink-0">
                    <DollarSign className="w-4 h-4 md:w-5 md:h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] md:text-xs font-semibold text-slate-500 uppercase tracking-wider mb-0.5">Salary</div>
                    <div className="font-medium text-slate-900 text-xs md:text-base truncate">{job.salary}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-1.5 md:gap-2">
              {job.seniority && (
                <span className="px-2 md:px-3 py-1 md:py-1.5 bg-blue-50 text-blue-700 text-xs md:text-sm font-semibold rounded-lg border border-blue-100">
                  {job.seniority}
                </span>
              )}
              <span className="px-2 md:px-3 py-1 md:py-1.5 bg-green-50 text-green-700 text-xs md:text-sm font-semibold rounded-lg border border-green-100">
                {job.employmentType}
              </span>
              <span className="px-2 md:px-3 py-1 md:py-1.5 bg-purple-50 text-purple-700 text-xs md:text-sm font-semibold rounded-lg border border-purple-100">
                {job.workMode}
              </span>
            </div>

            {/* Apply Button (Top) */}
            {job.applyUrl && (
              <button
                onClick={handleApply}
                className="w-full btn-primary py-3 md:py-4 text-sm md:text-lg shadow-lg shadow-blue-200 flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4 md:w-5 md:h-5" />
                Apply Now
              </button>
            )}

            {/* Job Description */}
            {job.description && (
              <div className="space-y-2 md:space-y-4">
                <h3 className="text-base md:text-xl font-bold text-slate-900">About the Role</h3>
                <div className="prose prose-slate max-w-none text-slate-600 leading-relaxed text-xs md:text-base">
                  <p className="whitespace-pre-line">{job.description}</p>
                </div>
              </div>
            )}

            {/* Requirements */}
            {job.requirements && job.requirements.length > 0 && (
              <div className="space-y-2 md:space-y-4">
                <h3 className="text-base md:text-xl font-bold text-slate-900">Requirements</h3>
                <ul className="space-y-2 md:space-y-3">
                  {job.requirements.map((req, index) => (
                    <li key={index} className="flex items-start gap-2 md:gap-3 bg-slate-50 p-3 md:p-4 rounded-xl border border-slate-100">
                      <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5 font-bold text-[10px] md:text-xs">
                        {index + 1}
                      </div>
                      <span className="text-slate-700 font-medium text-xs md:text-base">{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Responsibilities */}
            {job.responsibilities && job.responsibilities.length > 0 && (
              <div className="space-y-2 md:space-y-4">
                <h3 className="text-base md:text-xl font-bold text-slate-900">Responsibilities</h3>
                <ul className="space-y-2 md:space-y-3">
                  {job.responsibilities.map((resp, index) => (
                    <li key={index} className="flex items-start gap-2 md:gap-3">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-1.5 md:mt-2.5 flex-shrink-0 shadow-sm" />
                      <span className="text-slate-700 leading-relaxed text-xs md:text-base">{resp}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Benefits */}
            {job.benefits && job.benefits.length > 0 && (
              <div className="space-y-2 md:space-y-4">
                <h3 className="text-base md:text-xl font-bold text-slate-900">Benefits</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                  {job.benefits.map((benefit, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 md:gap-3 bg-white p-3 md:p-4 rounded-xl border border-slate-200 shadow-sm hover:border-blue-200 hover:shadow-md transition-all"
                    >
                      <div className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0" />
                      <span className="text-slate-700 font-medium text-xs md:text-base">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t border-slate-100 p-3 md:p-6 flex flex-col sm:flex-row justify-end gap-2 md:gap-4 rounded-b-xl md:rounded-b-2xl z-10">
            <button
              onClick={onClose}
              className="btn-secondary px-4 md:px-6 py-2 md:py-2.5 text-sm md:text-base order-2 sm:order-1"
            >
              Close
            </button>
            {job.applyUrl && (
              <button
                onClick={handleApply}
                className="btn-primary px-4 md:px-8 py-2 md:py-2.5 flex items-center justify-center gap-2 text-sm md:text-base order-1 sm:order-2"
              >
                <ExternalLink className="w-4 h-4" />
                <span className="hidden sm:inline">Apply for this Position</span>
                <span className="sm:hidden">Apply Now</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
